import { fromZonedTime, getTimezoneOffset } from 'date-fns-tz';

import { OrderStateV2, JobDraftV2, ShiftDraftV2 } from '../context';

import { CreateOrderPayloadV2, LocationListItem, SurgeRateV2 } from './types';

// Shifts are held in state as naive wall-time strings ("YYYY-MM-DDTHH:MM:SS")
// representing the time at the job's location. The backend stores instants in
// UTC, so we resolve the wall time to UTC at the submission boundary using the
// location's IANA timezone. Without this, the backend interprets the naive
// string as UTC and the displayed shift drifts by the offset (e.g. 6 AM EDT
// rendered as 2 AM EDT).
const MS_PER_HOUR = 60 * 60 * 1000;

const resolveTimezone = (tz: string | null | undefined): string =>
  tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

const toUtcIso = (wallTime: string, timezone: string): string =>
  fromZonedTime(wallTime, timezone).toISOString();

const offsetHoursFor = (wallTime: string, timezone: string): number => {
  const instant = fromZonedTime(wallTime, timezone);
  return getTimezoneOffset(timezone, instant) / MS_PER_HOUR;
};

const surgeRateForDate = (
  surgeRates: SurgeRateV2[],
  shift: ShiftDraftV2,
): SurgeRateV2 | null => {
  // Shifts hold naive wall-time strings ("YYYY-MM-DDTHH:MM:SS"); the date
  // portion already matches the local-calendar date the user selected.
  const dateKey = shift.start_at.slice(0, 10);
  return surgeRates.find((sr) => sr.date === dateKey) || null;
};

// Splits a job's shifts by their surge-rate date, mirroring the legacy
// CreateGigOrder flow where each per-day schedule with a holiday rate is
// emitted as its own JobInput with `is_holiday: true` and surge-multiplied
// rates. Shifts that fall on non-surge dates stay grouped on the base job.
//
// Returns an array of jobs:
//   - One "base" job carrying the non-holiday shifts (omitted if all shifts
//     fall on surge dates).
//   - One job per distinct surge-rate id with `is_holiday: true`,
//     `pay_rate`/`cost_rate` multiplied by the surge rate, and
//     `original_pay_rate`/`original_cost_rate` preserved.
const splitJobBySurgeRates = (
  job: JobDraftV2,
  surgeRates: SurgeRateV2[],
): JobDraftV2[] => {
  if (!surgeRates.length) return [job];

  const baseShifts: ShiftDraftV2[] = [];
  const surgeBuckets = new Map<
    number,
    { rate: SurgeRateV2; shifts: ShiftDraftV2[] }
  >();

  for (const shift of job.shifts || []) {
    const surge = surgeRateForDate(surgeRates, shift);
    if (surge) {
      const bucket = surgeBuckets.get(surge.id) || { rate: surge, shifts: [] };
      bucket.shifts.push(shift);
      surgeBuckets.set(surge.id, bucket);
    } else {
      baseShifts.push(shift);
    }
  }

  if (surgeBuckets.size === 0) return [job];

  const basePay = job.pay_rate;
  const baseCost = job.cost_rate;
  const out: JobDraftV2[] = [];

  if (baseShifts.length > 0) {
    out.push({
      ...job,
      shifts: baseShifts,
      is_holiday: false,
      original_pay_rate: basePay,
      original_cost_rate: baseCost,
    });
  }

  surgeBuckets.forEach(({ rate, shifts }) => {
    out.push({
      ...job,
      shifts,
      is_holiday: true,
      pay_rate: Math.round(basePay * rate.rate),
      cost_rate: Math.round(baseCost * rate.rate),
      original_pay_rate: basePay,
      original_cost_rate: baseCost,
    });
  });

  return out;
};

export const buildCreatePayload = (
  state: OrderStateV2,
  locations: LocationListItem[] = [],
  surgeRates: SurgeRateV2[] = [],
): CreateOrderPayloadV2 => ({
  account_id: state.billingAccount?.id || 0,
  order_type: (state.orderType || 'GIG') as 'GIG' | 'LTA',
  verification_enabled: !!state.verificationEnabled,
  jobs: state.jobs.flatMap((job) => {
    const location = locations.find((loc) => loc.id === job.address_id);
    const tz = resolveTimezone(location?.timezone);
    const split = splitJobBySurgeRates(job, surgeRates);
    return split.map((variant) => {
      const { staffing_strategy, auto_fill, ...rest } = variant;
      const visibility =
        staffing_strategy === 'OPEN_JOB_POST' ? 'PUBLIC' : 'PRIVATE';
      return {
        ...rest,
        shifts: (variant.shifts || []).map((shift) => ({
          ...shift,
          start_at: toUtcIso(shift.start_at, tz),
          end_at: toUtcIso(shift.end_at, tz),
          time_zone_offset: offsetHoursFor(shift.start_at, tz),
        })),
        job_type: state.orderType || variant.job_type,
        visibility,
        // v1 semantics: `publish: true` on a PRIVATE job tells the backend
        // to schedule a fallback public posting after invites/hires run
        // (only if the job is still unfilled). On PUBLIC jobs the field
        // has no effect — the job is posted immediately at creation.
        publish: visibility === 'PRIVATE' ? auto_fill === true : false,
      };
    });
  }),
});
